import {
  BottomActions,
  Box,
  Nav,
  useMediaQuery,
  PageHeader
} from '@dagens/carrot';
import { useTranslation } from '@dagens/frontend-i18n';
import { Form } from '@dagens/frontend-forms';
import { CountryCode } from '@dagensmat/core';
import { Page } from '../../../../components/page';
import REQ from '../../../../utils/REQ';
import {
  BillingFormValues,
  CustomerBillingForm
} from '../../../components/customer-billing-form';
import { ManagedCustomer } from '../../../../api';
import { useFetchCustomer } from '../shared/use-fetch-customer';
import { useOnSave } from './use-on-save';
import { BillingBottomActions } from './bottom-actions';

const consumerToForm = (consumer: ManagedCustomer) => {
  return {
    billingName: consumer.billingAccount.name,
    department: consumer.billingAccount.department,
    eanNumber: consumer.billingAccount.eanNumber,
    primaryEmail: consumer.billingAccount.emails.at(0) ?? null,
    reminderEmail: consumer.billingAccount.reminderEmail,
    billingAddress: consumer.billingAccount.address
      ? {
          addressLine1: consumer.billingAccount.address.addressLine1 || '',
          addressLine2: consumer.billingAccount.address.addressLine2 || '',
          postalCode: consumer.billingAccount.address.postalCode || '',
          city: consumer.billingAccount.address.city || '',
          country: consumer.billingAccount.address.country as CountryCode
        }
      : null
  } satisfies BillingFormValues;
};

type Props = {
  consumer: ManagedCustomer;
};

const PageContent = ({ consumer }: Props) => {
  const { desktopUp } = useMediaQuery();
  const { t } = useTranslation();
  const { onSave, saveReq } = useOnSave(consumer._id);

  const title = t('producer:EditCustomerEditBillingInformation');
  const subtitle = consumer.name;

  return (
    <Form.Provider defaultValue={consumerToForm(consumer)}>
      <Page
        navMobileText={
          <>
            <Nav.ContextTitle>{title}</Nav.ContextTitle>
            <Nav.ContextSubtitle>{subtitle}</Nav.ContextSubtitle>
          </>
        }
        header={
          desktopUp && (
            <Box.FullWidth>
              <PageHeader title={title} subtitle={subtitle} />
            </Box.FullWidth>
          )
        }
        bottom={
          <BottomActions border>
            <BillingBottomActions
              consumerId={consumer._id}
              saveReq={saveReq}
              onSubmit={onSave}
            />
          </BottomActions>
        }
      >
        <Box.FormWrapper>
          <Form onSubmit={onSave}>
            <CustomerBillingForm
              variant="edit"
              organizationNumber={consumer.billingAccount.organizationNumber}
            />
          </Form>
        </Box.FormWrapper>
      </Page>
    </Form.Provider>
  );
};

export const EditCustomerBillingPage = () => {
  const { req, consumer } = useFetchCustomer();

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }

  if (!consumer) {
    return <Page.NotFound />;
  }

  return <PageContent consumer={consumer} />;
};
